import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { api, type Todo } from "../lib/api";

export const Route = createFileRoute("/todos")({
  component: TodosPage,
});

type Filter = "all" | "active" | "completed";

function TodosPage() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [title, setTitle] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .listTodos()
      .then((data) => {
        if (!cancelled) setTodos(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const remaining = useMemo(
    () => todos.filter((t) => !t.isComplete).length,
    [todos],
  );
  const completed = todos.length - remaining;

  const visibleTodos = useMemo(() => {
    switch (filter) {
      case "active":
        return todos.filter((t) => !t.isComplete);
      case "completed":
        return todos.filter((t) => t.isComplete);
      default:
        return todos;
    }
  }, [todos, filter]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;
    try {
      const created = await api.createTodo(trimmed);
      setTodos((prev) => [created, ...prev]);
      setTitle("");
      setError(null);
    } catch (err: unknown) {
      setError(errorMessage(err));
    }
  }

  async function handleToggle(todo: Todo) {
    setBusyId(todo.id);
    try {
      const updated = await api.updateTodo(todo.id, {
        title: todo.title,
        isComplete: !todo.isComplete,
      });
      setTodos((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setError(null);
    } catch (err: unknown) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  function startEditing(todo: Todo) {
    setEditingId(todo.id);
    setEditingTitle(todo.title);
  }

  function cancelEditing() {
    setEditingId(null);
    setEditingTitle("");
  }

  async function saveEditing(todo: Todo) {
    const trimmed = editingTitle.trim();
    if (!trimmed) {
      cancelEditing();
      return;
    }
    if (trimmed === todo.title) {
      cancelEditing();
      return;
    }
    setBusyId(todo.id);
    try {
      const updated = await api.updateTodo(todo.id, {
        title: trimmed,
        isComplete: todo.isComplete,
      });
      setTodos((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setError(null);
      cancelEditing();
    } catch (err: unknown) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(todo: Todo) {
    setBusyId(todo.id);
    try {
      await api.deleteTodo(todo.id);
      setTodos((prev) => prev.filter((t) => t.id !== todo.id));
      setError(null);
    } catch (err: unknown) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="space-y-6">
      <div className="flex items-baseline justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Verkefni</h1>
        {!loading && todos.length > 0 && (
          <p className="text-sm text-slate-500">
            {remaining} eftir · {completed} lokið
          </p>
        )}
      </div>

      <form onSubmit={handleAdd} className="flex gap-2">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Nýtt verkefni…"
          maxLength={200}
          className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
        />
        <button
          type="submit"
          disabled={!title.trim()}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Bæta við
        </button>
      </form>

      {error && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {!loading && todos.length > 0 && (
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1 text-sm">
          {(
            [
              ["all", "Allt"],
              ["active", "Í vinnslu"],
              ["completed", "Lokið"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={
                filter === value
                  ? "flex-1 rounded-md bg-indigo-600 px-3 py-1.5 font-medium text-white"
                  : "flex-1 rounded-md px-3 py-1.5 font-medium text-slate-600 transition-colors hover:bg-slate-100"
              }
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-400">Hleð…</p>
      ) : todos.length === 0 ? (
        <p className="text-sm text-slate-400">Engin verkefni ennþá.</p>
      ) : visibleTodos.length === 0 ? (
        <p className="text-sm text-slate-400">
          Engin verkefni í þessum flokki.
        </p>
      ) : (
        <ul className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {visibleTodos.map((todo) => {
            const busy = busyId === todo.id;
            const editing = editingId === todo.id;
            return (
              <li
                key={todo.id}
                className="flex items-center gap-3 px-4 py-3 text-sm data-[busy=true]:opacity-50"
                data-busy={busy}
              >
                <input
                  type="checkbox"
                  checked={todo.isComplete}
                  disabled={busy || editing}
                  onChange={() => handleToggle(todo)}
                  className="h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/30"
                />

                {editing ? (
                  <input
                    autoFocus
                    value={editingTitle}
                    maxLength={200}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void saveEditing(todo);
                      if (e.key === "Escape") cancelEditing();
                    }}
                    className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                  />
                ) : (
                  <span
                    onDoubleClick={() => startEditing(todo)}
                    className={
                      todo.isComplete
                        ? "flex-1 truncate text-slate-400 line-through"
                        : "flex-1 truncate"
                    }
                  >
                    {todo.title}
                  </span>
                )}

                {editing ? (
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => void saveEditing(todo)}
                      disabled={busy}
                      className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Vista
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100"
                    >
                      Hætta við
                    </button>
                  </div>
                ) : (
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => startEditing(todo)}
                      disabled={busy}
                      className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                    >
                      Breyta
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(todo)}
                      disabled={busy}
                      className="rounded-md px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      Eyða
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
